/**
 * Safari 无痕等场景下，Hosted Checkout 跨站回跳可能剥光 query。
 * 下单时把后端 return_url / cancel_url（并合并落地五参）写入 sessionStorage，落地裸 path 后同源 replace 恢复。
 */

import {
    SESSION_KEY_HOSTED_CHECKOUT_PENDING_RETURN,
    SESSION_KEY_HOSTED_CHECKOUT_SUCCESS_CHAPTER,
} from '@/constants/storage';
import { EHostedCheckoutReturnQueryKey } from '@/lib/pay/hosted-checkout-return-params';
import { pickLandingPersistParams, withDyeQueryFromRecord } from '@/utils/router/with-dye-query';

import type { ICreateOrderData } from '@/apis/commerce';

/** 已确认支付成功的回站章节写入后，用于通知已挂载 ReaderCore 的同源事件。 */
export const PAY_SUCCESS_RETURN_CHAPTER_EVENT = 'h5r:pay-success-return-chapter';

export interface IPayPendingReturnUrls {
    orderId: string;
    successReturnUrl: string;
    cancelReturnUrl: string;
    /** 发起整页支付前所在的站内页面；回跳处理完毕后恢复到此处 */
    originReturnPath?: string;
    /** 跳转 Checkout 前快照的落地五参；回站清参时补回地址栏 */
    landingPersistQuery?: Record<string, string>;
}

export function persistPayReturnUrls(data: IPayPendingReturnUrls): void {
    if (typeof window === 'undefined' || !data.orderId || !data.successReturnUrl) {
        return;
    }
    try {
        window.sessionStorage.setItem(SESSION_KEY_HOSTED_CHECKOUT_PENDING_RETURN, JSON.stringify(data));
    } catch {
        // ignore
    }
}

/** 仅接受同源相对路径，避免 session 数据被篡改后成为开放重定向入口。 */
export function isSafeInternalPath(path: string): boolean {
    if (!path.startsWith('/') || path.startsWith('//')) {
        return false;
    }
    try {
        return new URL(path, window.location.origin).origin === window.location.origin;
    } catch {
        return false;
    }
}

/** 创单成功：快照五参并写入已合并的 return_url / cancel_url */
export function persistPayReturnUrlsFromCreateOrder(order: ICreateOrderData): void {
    const returnUrl = order.store_payload?.return_url?.trim();
    if (!order.order_id || !returnUrl) {
        return;
    }

    const landingPersistQuery = pickLandingPersistParams();
    const cancelUrl = order.store_payload?.cancel_url?.trim() ?? '';
    const hasPersist = Object.keys(landingPersistQuery).length > 0;
    const originReturnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    persistPayReturnUrls({
        orderId: order.order_id,
        successReturnUrl: withDyeQueryFromRecord(returnUrl, landingPersistQuery),
        cancelReturnUrl: cancelUrl ? withDyeQueryFromRecord(cancelUrl, landingPersistQuery) : '',
        originReturnPath: isSafeInternalPath(originReturnPath) ? originReturnPath : undefined,
        landingPersistQuery: hasPersist ? landingPersistQuery : undefined,
    });
}

export function readPayPendingReturnUrls(): IPayPendingReturnUrls | null {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        const raw = window.sessionStorage.getItem(SESSION_KEY_HOSTED_CHECKOUT_PENDING_RETURN);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as Partial<IPayPendingReturnUrls>;
        if (typeof parsed.orderId !== 'string' || typeof parsed.successReturnUrl !== 'string') {
            return null;
        }

        const rawQuery = parsed.landingPersistQuery;
        const landingPersistQuery =
            rawQuery && typeof rawQuery === 'object'
                ? pickLandingPersistParams(new URLSearchParams(rawQuery as Record<string, string>))
                : undefined;

        return {
            orderId: parsed.orderId,
            successReturnUrl: parsed.successReturnUrl,
            cancelReturnUrl: typeof parsed.cancelReturnUrl === 'string' ? parsed.cancelReturnUrl : '',
            originReturnPath:
                typeof parsed.originReturnPath === 'string' && isSafeInternalPath(parsed.originReturnPath)
                    ? parsed.originReturnPath
                    : undefined,
            landingPersistQuery:
                landingPersistQuery && Object.keys(landingPersistQuery).length > 0 ? landingPersistQuery : undefined,
        };
    } catch {
        return null;
    }
}

export function clearPayPendingReturnUrls(): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.sessionStorage.removeItem(SESSION_KEY_HOSTED_CHECKOUT_PENDING_RETURN);
    } catch {
        // ignore
    }
}

/** 标记成功回站后需要展示的已解锁章节；与取消回站快照分开存储。 */
export function persistPaySuccessReturnChapter(chapterId?: string): void {
    if (typeof window === 'undefined' || !chapterId) {
        return;
    }
    try {
        window.sessionStorage.setItem(SESSION_KEY_HOSTED_CHECKOUT_SUCCESS_CHAPTER, chapterId);
    } catch {
        // ignore
    }

    // ReaderCore 可能早于订单确认挂载；仅写 sessionStorage 不会使已挂载组件重新读取。
    window.dispatchEvent(
        new CustomEvent<{ chapterId: string }>(PAY_SUCCESS_RETURN_CHAPTER_EVENT, {
            detail: { chapterId },
        })
    );
}

/** 读取成功回站章节；由 ReaderCore 在定位完成后消费。 */
export function readPaySuccessReturnChapter(): string {
    if (typeof window === 'undefined') {
        return '';
    }
    try {
        return window.sessionStorage.getItem(SESSION_KEY_HOSTED_CHECKOUT_SUCCESS_CHAPTER) ?? '';
    } catch {
        return '';
    }
}

export function clearPaySuccessReturnChapter(): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.sessionStorage.removeItem(SESSION_KEY_HOSTED_CHECKOUT_SUCCESS_CHAPTER);
    } catch {
        // ignore
    }
}

/** 从跳转前 URL 快照读取阅读器目标章节，供回站后持续跳过首付费章守卫。 */
export function readPayPendingReturnChapter(): string {
    const originReturnPath = readPayPendingReturnUrls()?.originReturnPath;
    if (!originReturnPath) {
        return '';
    }
    try {
        const url = new URL(originReturnPath, window.location.origin);
        return url.pathname === '/reader' ? (url.searchParams.get('chapter_id') ?? '') : '';
    } catch {
        return '';
    }
}

/** 回站清洗网关 query 后，用跳转前快照（或当前 URL）补回落地五参 */
export function mergeLandingPersistAfterCheckoutReturn(cleanUrl: string): string {
    const pending = readPayPendingReturnUrls();
    return withDyeQueryFromRecord(cleanUrl, pending?.landingPersistQuery ?? pickLandingPersistParams());
}

function hasHostedCheckoutReturnOption(search: string): boolean {
    const option = new URLSearchParams(search).get(EHostedCheckoutReturnQueryKey.Option);
    return option === 'success' || option === 'approve' || option === 'cancel' || option === 'cancelled';
}

function pathnameFromReturnUrl(returnUrl: string): string | null {
    try {
        return new URL(returnUrl, window.location.origin).pathname;
    } catch {
        return null;
    }
}

/**
 * 检测当前页面是否通过浏览器"后退"导航到达（手势返回、返回按钮等）。
 * 用于区分：
 *   - TYPE_NAVIGATE (0)：正常/剥参回跳 → 需要恢复 success URL
 *   - TYPE_BACK_FORWARD (2)：用户手势返回 → 按取消处理，不应触发 success 恢复
 */
export function isBackForwardNavigation(): boolean {
    if (typeof window === 'undefined' || typeof performance === 'undefined') {
        return false;
    }
    try {
        const entries = performance.getEntriesByType?.('navigation') as PerformanceNavigationTiming[] | undefined;
        if (entries?.length) {
            return entries[0].type === 'back_forward';
        }
        return performance.navigation?.type === 2;
    } catch {
        return false;
    }
}

/**
 * 当前页无 `option` 且 session 中有 pending return_url 时，同源 replace 恢复完整 query。
 * 当前 pathname 必须与该订单保存的成功或取消回跳路径一致，支持任意站内页面。
 * @returns true 表示已触发 replace，调用方应中止后续逻辑（页面将刷新）
 */
export function tryRestoreStrippedPayReturnUrl(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    const { pathname, search, hash } = window.location;
    if (hasHostedCheckoutReturnOption(search)) {
        return false;
    }

    const pending = readPayPendingReturnUrls();
    if (!pending) {
        return false;
    }

    // 手势返回（back_forward 导航）：用户从 Hosted Checkout 手势返回，不是 Safari 剥参场景，
    // 不应强行替换成 successReturnUrl，由 handler 的取消逻辑兜底。
    if (isBackForwardNavigation()) {
        return false;
    }

    const expectedPath = pathnameFromReturnUrl(pending.successReturnUrl);
    if (!expectedPath || expectedPath !== pathname) {
        const cancelPath = pending.cancelReturnUrl ? pathnameFromReturnUrl(pending.cancelReturnUrl) : null;
        if (cancelPath !== pathname) {
            return false;
        }
    }

    let restoreUrl: URL;
    try {
        restoreUrl = new URL(pending.successReturnUrl, window.location.origin);
        if (restoreUrl.origin !== window.location.origin) {
            restoreUrl = new URL(
                `${restoreUrl.pathname}${restoreUrl.search}${restoreUrl.hash}`,
                window.location.origin
            );
        }
    } catch {
        return false;
    }

    if (!hasHostedCheckoutReturnOption(restoreUrl.search)) {
        return false;
    }

    const restorePathAndSearch = withDyeQueryFromRecord(
        `${restoreUrl.pathname}${restoreUrl.search}${restoreUrl.hash}`,
        pending.landingPersistQuery
    );
    const current = `${pathname}${search}${hash}`;
    if (restorePathAndSearch === current) {
        return false;
    }

    window.location.replace(restorePathAndSearch);
    return true;
}
